import React from 'react'
import {Box} from '../../webui/Box'
import {Button} from '../../webui/Button'
import {Typography} from '../../webui/Typography'
import {ToggleOpen} from '../../webui/ToggleOpen'
import {CONTRACT_TYPE_ORDER, CONTRACT_TYPES} from '../../common/domain/contractType'
import {GQL_ContractType} from '../../generated/graphql'
import {Select} from '../../webui/inputs/Select'
import {MenuItem} from '../../webui/MenuItem'

export type DeprecatedMode = 'exclude' | 'only' | 'all'
export type ActiveMode = 'all' | 'onlyActive' | 'excludeActive'

export type BoxesPageFilterState = {
  open: boolean
  search: string
  activeMode: ActiveMode
  tempClosedStatus: 'yes' | 'no' | 'all'
  deprecatedMode: DeprecatedMode
  contractTypes: GQL_ContractType[]
  includeContractTypeNotSet: boolean
}

type Props = {
  filter: BoxesPageFilterState
  setFilter: (next: BoxesPageFilterState) => void
  onClear: () => void
  rowCount: number
}

export const BoxesPageFilter: React.FC<Props> = ({filter, setFilter, onClear, rowCount}) => {
  const toggleContractType = (ct: GQL_ContractType) => {
    const exists = filter.contractTypes.includes(ct)
    const next = exists ? filter.contractTypes.filter((x: GQL_ContractType) => x !== ct) : [...filter.contractTypes, ct]

    setFilter({...filter, contractTypes: next})
  }

  const setDeprecatedMode = (mode: DeprecatedMode) => {
    setFilter({
      ...filter,
      deprecatedMode: mode,
      // ALL implies no other status filtering
      activeMode: mode === 'all' ? 'all' : filter.activeMode,
    })
  }

  const setActiveStatus = (value: ActiveMode) => {
    setFilter({
      ...filter,
      activeMode: value,
      // selecting Active deselects ALL deprecated mode
      deprecatedMode: value !== 'all' && filter.deprecatedMode === 'all' ? 'exclude' : filter.deprecatedMode,
    })
  }

  const setTempClosedStatus = (value: 'yes' | 'no' | 'all') => {
    setFilter({
      ...filter,
      tempClosedStatus: value,
    })
  }

  const toggleOpen = () => setFilter({...filter, open: !filter.open})

  return (
    <Box>
      {/* CLICKABLE HEADER */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={toggleOpen}
        sx={{
          cursor: 'pointer',
          px: 0.5,
          py: 0.5,
          borderRadius: 1,
          transition: 'background-color 120ms ease',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Typography variant="h6">Filters</Typography>

        {/* Chevron remains visual; click handled by parent */}
        <ToggleOpen open={filter.open} onToggle={toggleOpen} />
      </Box>

      {filter.open && (
        <Box mt={1.5} display="flex" flexDirection="column" gap={1.5}>
          {/* STATUS */}
          <Box>
            <Typography variant="body2" sx={{mb: 0.5}}>
              Status
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Select
                label="Temp closed"
                value={filter.tempClosedStatus}
                onChange={(e) => setTempClosedStatus(e.target.value as 'yes' | 'no' | 'all')}
                variant={'outlined'}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
              <Select label="Active" value={filter.activeMode} onChange={(e) => setActiveStatus(e.target.value as ActiveMode)} variant={'outlined'}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="onlyActive">Only active</MenuItem>
                <MenuItem value="excludeActive">Exclude active</MenuItem>
              </Select>

              <Select
                label="Deprecated"
                value={filter.deprecatedMode}
                onChange={(e) => setDeprecatedMode(e.target.value as DeprecatedMode)}
                variant={'outlined'}
              >
                <MenuItem value="exclude">Exclude deprecated</MenuItem>
                <MenuItem value="only">Only deprecated</MenuItem>
                <MenuItem value="all">All</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* CONTRACT TYPE */}
          <Box>
            <Typography variant="body2" sx={{mb: 0.5}}>
              Contract type
            </Typography>

            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                size="small"
                variant={filter.includeContractTypeNotSet ? 'contained' : 'outlined'}
                onClick={() =>
                  setFilter({
                    ...filter,
                    includeContractTypeNotSet: !filter.includeContractTypeNotSet,
                  })
                }
                sx={{'&:hover': {backgroundColor: 'action.hover'}}}
              >
                Not set
              </Button>

              {CONTRACT_TYPE_ORDER.map((ct) => {
                const selected = filter.contractTypes.includes(ct)
                return (
                  <Button
                    key={ct}
                    size="small"
                    variant={selected ? 'contained' : 'outlined'}
                    onClick={() => toggleContractType(ct)}
                    sx={{'&:hover': {backgroundColor: 'action.hover'}}}
                  >
                    {CONTRACT_TYPES[ct].label}
                  </Button>
                )
              })}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{mt: 1}}>
              Filter result: {rowCount} boxes
            </Typography>
          </Box>

          {/* CLEAR */}
          <Box display="flex" justifyContent="flex-end">
            <Button size="small" variant="outlined" onClick={onClear} sx={{'&:hover': {backgroundColor: 'action.hover'}}}>
              Reset And Close Filter
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  )
}
